import { Button, Col, message, Modal, Row } from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const MotionRowComponent = motion(Row);
const MotionColComponent = motion(Col);

const CustomerRegistrationOrderModal = (props) => {
	const {
		publicSettings,
		isCustomerRegistrationOrderModal,
		hideCustomerRegistrationOrderModal,
	} = props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [customerRegistrations, setCustomerRegistrations] = useState([]);
	const [orderedCustomerRegistrations, setOrderedCustomerRegistrations] =
		useState([]);

	useQuery(
		[API.QUERY_KEY_ADMIN_CUSTOMER_REGISTRATIONS],
		() => API.ADMIN_GET_CUSTOMER_REGISTRATIONS(),
		{
			enabled: isCustomerRegistrationOrderModal,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setCustomerRegistrations(response?.data || []);
					setOrderedCustomerRegistrations(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const updateCustomerRegistrationOrderMutation = useMutation(
		API.ADMIN_UPDATE_CUSTOMER_REGISTRATION_ORDER,
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					message.success(COMMONS.SUCCESS_UPDATE_MSG);
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_CUSTOMER_REGISTRATIONS],
					});
					hideCustomerRegistrationOrderModal();
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	useEffect(() => {
		if (!isCustomerRegistrationOrderModal) {
			setCustomerRegistrations([]);
		}
	}, [isCustomerRegistrationOrderModal]);

	const handleCustomerRegistrationOrder = () => {
		const paramData = {
			customerRegistrations: orderedCustomerRegistrations.map((q, i) => ({
				customerRegistrationId: q?.customerRegistrationId,
				showOrder: i,
			})),
		};

		updateCustomerRegistrationOrderMutation.mutate(paramData);
	};

	const customerRegistrationOnDragEndHandle = (result) => {
		if (!result.destination) {
			return;
		} else {
			const reOrderedCustomerRegistrations = COMMONS.RE_ORDER(
				orderedCustomerRegistrations,
				result.source.index,
				result.destination.index,
			);

			setOrderedCustomerRegistrations(reOrderedCustomerRegistrations);
		}
	};

	const handleRevert = () => {
		setOrderedCustomerRegistrations(customerRegistrations);
	};

	return (
		<>
			<Modal
				open={isCustomerRegistrationOrderModal}
				onCancel={hideCustomerRegistrationOrderModal}
				title="質問表示順"
				footer={null}
				destroyOnClose
				maskClosable={false}
				centered
				width={720}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
			>
				<motion.div
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
					initial="hidden"
					animate="show"
					exit="hidden"
				>
					<motion.div
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
						className="flex justify-end mb-4"
					>
						<TapAnimationComponent>
							<Button
								className="m-1"
								size="large"
								danger
								onClick={handleRevert}
							>
								元に戻す
							</Button>
						</TapAnimationComponent>
						<TapAnimationComponent>
							<Button
								type="primary"
								className="m-1 w-32"
								size="large"
								onClick={handleCustomerRegistrationOrder}
							>
								保存
							</Button>
						</TapAnimationComponent>
					</motion.div>
					<motion.div
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
						className="flex justify-center rounded mb-8 p-4"
						style={{
							color: publicSettings?.PRIMARY_COLOR,
							backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
						}}
					>
						<span>質問を上下にドラッグして並べ替えます</span>
					</motion.div>
				</motion.div>
				<MotionRowComponent
					gutter={[32, 32]}
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
					initial="hidden"
					animate="show"
					exit="hidden"
				>
					<MotionColComponent
						xs={24}
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
					>
						{orderedCustomerRegistrations &&
						orderedCustomerRegistrations?.length > 0 ? (
							<DragDropContext onDragEnd={customerRegistrationOnDragEndHandle}>
								<Droppable droppableId="droppableQuestion" direction="vertical">
									{(provided, snapshot) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
											className="flex flex-col"
										>
											{orderedCustomerRegistrations.map(
												(customerRegistration, index) => (
													<Draggable
														key={
															customerRegistration?.customerRegistrationId + ""
														}
														draggableId={
															customerRegistration?.customerRegistrationId + ""
														}
														index={index}
													>
														{(provided, snapshot) => (
															<div
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																className="mb-4 cursor-pointer"
															>
																<div
																	className="flex items-center px-4 border rounded w-full"
																	style={{
																		borderColor: publicSettings?.PRIMARY_COLOR,
																		backgroundColor:
																			publicSettings?.PRIMARY_LIGHT_COLOR,
																		color: publicSettings?.PRIMARY_COLOR,
																	}}
																>
																	<p className="p-4 font-bold whitespace-pre-wrap">
																		{`${index + 1}）${
																			customerRegistration?.label || ""
																		}`}
																	</p>
																</div>
															</div>
														)}
													</Draggable>
												),
											)}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						) : (
							<p className="text-center">質問がありません。</p>
						)}
					</MotionColComponent>
				</MotionRowComponent>
			</Modal>
		</>
	);
};

export default CustomerRegistrationOrderModal;
